import { useEffect, useState, useCallback } from 'react';

export type AutoCloseLinkProps =
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    as?: React.ElementType;

    /**
     * Accept incoming message from different source (not the child window)
     */
    skipSameSource?: boolean;

    /**
     * Accept incoming message from different origin
     */
    skipSameOrigin?: boolean;

    /**
     * Called when target href is closed
     */
    onClose?: () => void;

    /**
     * Return true if incoming message event should close target href
     */
    onMessageCheck: (event: MessageEvent) => boolean;
  };

/**
 * Anchor element that opens href in a new tab
 * and automatically closes based on messages received in the window
 */
function AutoCloseLink(
  props: AutoCloseLinkProps
): React.ReactElement<HTMLAnchorElement> {
  const {
    children,
    href,
    onMessageCheck,
    onClose,
    skipSameOrigin = false,
    skipSameSource = false,
    as: Component = 'a',
    ...otherProps
  } = props;
  const [childWindow, setChildWindow] = useState<WindowProxy | null>();

  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    setChildWindow(window.open(event.currentTarget.href));
  };

  const onMessage = useCallback(
    (event: MessageEvent) => {
      const isSameSource = skipSameSource || event.source === childWindow;
      const isSameOrigin =
        skipSameOrigin || (href && event.origin === new URL(href).origin);

      if (isSameOrigin && isSameSource && onMessageCheck(event)) {
        if (childWindow) {
          childWindow?.close();
          setChildWindow(undefined);
        }

        if (onClose) {
          onClose();
        }
      }
    },
    [href, childWindow, onMessageCheck, onClose, skipSameSource, skipSameOrigin]
  );

  useEffect(() => {
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  return (
    <Component {...otherProps} href={href?.toString()} onClick={onClick}>
      {children}
    </Component>
  );
}

export { AutoCloseLink };
